import { useEffect, useMemo, useState, type JSX, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  IconSpark, IconSettings, IconArrowRight, IconMessage, IconUpload, IconActivity,
  IconRefresh, IconGrid, IconGlobe, IconCopy, IconFlag, IconFile, IconHeadset,
  IconClock, IconStop
} from "../components/icons";
import { SelectionLayer, type AskMode } from "./SelectionLayer";
import { AskDock, type PendingAsk } from "./AskDock";
import {
  clearAnchorHighlight, hideAnchorHighlight, scrollToAnchorHighlight, setAnchorHighlight,
  showAnchorHighlight
} from "./anchorHighlight";
import { normalizeAskStage, stageAtLeast, type AskStage } from "./askStage";
import "../styles/guides.css";

/** Manifest icon name -> icon component, so each nav page shows an icon. */
const PAGE_ICONS: Record<string, (p: { size?: number }) => JSX.Element> = {
  spark: IconSpark, settings: IconSettings, "arrow-right": IconArrowRight,
  message: IconMessage, upload: IconUpload, activity: IconActivity,
  refresh: IconRefresh, grid: IconGrid, globe: IconGlobe, copy: IconCopy,
  flag: IconFlag, file: IconFile, headset: IconHeadset, clock: IconClock,
  stop: IconStop
};

/**
 * Guides reader (FEATURE_MANUAL_READER). Renders a product manual as a
 * documentation surface: top tabs + grouped left sidebar + a page pane (title +
 * subtitle + markdown body + images) + an on-page TOC. Target-agnostic: it reads
 * the TOC manifest and pages from /api/guides/<manual>/... When the flag is off
 * the backend routes are absent, so the toc fetch 404s and we redirect home.
 */

/** Fallback manual id when /healthz omits manualReaderManual (older server). */
const DEFAULT_MANUAL = "cyclecare";

/** #01: the first-touch hint is shown once, then remembered (per browser). */
const HINT_SEEN_KEY = "guides-ask-hint-seen";

interface TocPage {
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
  diataxis?: string;
  status?: string;
  /** Source markdown file — the stable docId link between a citation and its
   *  page (title matching is the fragile fallback; see design review #03). */
  file?: string;
  /** HTTP method for an API Reference endpoint page — shown as a sidebar badge. */
  method?: string;
}
interface TocSection {
  id: string;
  title: string;
  pages: TocPage[];
}
interface TocTab {
  id: string;
  label: string;
  active?: boolean;
  disabled?: boolean;
  /** When set, clicking this tab switches the reader to that manual (e.g. the
   *  API Reference surface). Makes the top tabs functional, config-driven. */
  manual?: string;
}
interface Toc {
  manual: string;
  title: string;
  /** KB source the manual was ingested as — passed as manualSource for in-doc ask. */
  kbSource?: string;
  /** "api-reference" surfaces have many resource groups -> collapsible sidebar. */
  surface?: string;
  /** Host/product branding for the topbar — a manifest may override the host
   *  default so a third product's manual never wears the wrong brand (F1.1). */
  brand?: { product?: string; accent?: string };
  /** The assistant answering in-doc asks — name/avatar are content, not code (F1.2). */
  assistant?: { name?: string; avatar?: string };
  tabs: TocTab[];
  sections: TocSection[];
}
interface PageData {
  id: string;
  title: string;
  subtitle: string;
  markdown: string;
  status: string;
}

/** Stable anchor id from heading text (Chinese-safe). */
function slug(text: string): string {
  return text.trim().replace(/\s+/g, "-").replace(/[^\p{L}\p{N}-]/gu, "");
}

/** Flatten ReactMarkdown heading children into plain text for the id. */
function textOf(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (node && typeof node === "object" && "props" in node) {
    return textOf((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
}

export function GuidesReader(): JSX.Element {
  // The manual is in the URL (`/guides/:manual/:pageId`) so navigation between a
  // manual's pages — and between manuals (Guides <-> API Reference) — never loses
  // the surface, and a page is shareable/bookmarkable. The bare `/guides`
  // redirects to the configured default manual once healthz resolves.
  const { manual: manualParam, pageId } = useParams();
  const navigate = useNavigate();
  const [toc, setToc] = useState<Toc | null>(null);
  const [page, setPage] = useState<PageData | null>(null);
  const [notFound, setNotFound] = useState(false);
  // In-doc ask (FEATURE_MANUAL_ASK): gated by /healthz; a pending ask opens the dock.
  const [askEnabled, setAskEnabled] = useState(false);
  // Staged rollout slice (1a/1b/1c/2, MANUAL_ASK_STAGE) — later slices gate on it.
  const [askStage, setAskStage] = useState<AskStage>("1a");
  const [pendingAsk, setPendingAsk] = useState<PendingAsk | null>(null);
  const [dockOpen, setDockOpen] = useState(false);
  // #03: a pending citation deep-link — consumed once its page has rendered.
  const [jumpAnchor, setJumpAnchor] = useState<{ page: string; heading: string } | null>(null);
  // #02 (stage 1b+): closing the dock hides it — the conversation survives. The
  // dock mounts on the first ask and stays mounted for the rest of the visit.
  const [dockEverOpened, setDockEverOpened] = useState(false);
  // #01 (stage 1b+): one-time "you can select text and ask" hint.
  const [hintSeen, setHintSeen] = useState(() => {
    try {
      return localStorage.getItem(HINT_SEEN_KEY) === "1";
    } catch {
      return true;
    }
  });
  // Collapsed sidebar groups (API Reference has many resource groups). null until
  // initialized from the toc; user toggles override.
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);
  // #05: on small screens, ask is only usable as a bottom sheet (stage 1c+).
  // Below 1c a narrow viewport gets the honest tier-A degrade, never a dock
  // that squeezes the prose to nothing.
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 760px)");
    const onChange = (): void => setIsNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const manualId = manualParam ?? null;

  // Switching manuals resets the whole ask surface: the conversation, its
  // anchors, and the pending ask all belong to the previous manual.
  useEffect(() => {
    setDockOpen(false);
    setDockEverOpened(false);
    setPendingAsk(null);
    clearAnchorHighlight();
  }, [manualId]);

  const atLeast1c = stageAtLeast(askStage, "1c");
  /** Ask is live here: flag on AND (desktop, or mobile once the 1c sheet exists). */
  const askActive = askEnabled && (!isNarrow || atLeast1c);
  /** #05 tier-A: mobile below 1c — read-only with an honest "use desktop" note. */
  const askDegraded = askEnabled && isNarrow && !atLeast1c;

  // Resolve the flag + the configured default manual; redirect bare /guides to it.
  useEffect(() => {
    let live = true;
    void fetch("/healthz")
      .then((r) =>
        r.ok
          ? (r.json() as Promise<{
              features?: { manualReader?: boolean; manualAsk?: boolean; manualAskStage?: string };
              manualReaderManual?: string;
            }>)
          : null
      )
      .then((h) => {
        if (!live) return;
        if (h?.features?.manualReader !== true) {
          navigate("/", { replace: true });
          return;
        }
        setAskEnabled(h.features?.manualAsk === true);
        setAskStage(normalizeAskStage(h.features?.manualAskStage));
        if (manualParam === undefined) {
          navigate(`/guides/${encodeURIComponent(h.manualReaderManual ?? DEFAULT_MANUAL)}`, { replace: true });
        }
      })
      .catch(() => {
        if (live) navigate("/", { replace: true });
      });
    return () => {
      live = false;
    };
  }, [navigate, manualParam]);

  function onAsk(passage: string, mode: AskMode, range?: Range): void {
    const key = Math.floor(performance.now());
    // #04/#08: pin the passage as this turn's anchor highlight, keyed so a
    // historical turn's 「引自」 chip can scroll back to ITS passage.
    if (range) setAnchorHighlight(range, key);
    setDockOpen(true);
    setDockEverOpened(true);
    setPendingAsk({ passage, mode, key });
    // #01: the user has discovered select-to-ask — retire the hint.
    markHintSeen();
  }

  function markHintSeen(): void {
    setHintSeen(true);
    try {
      localStorage.setItem(HINT_SEEN_KEY, "1");
    } catch {
      /* private mode etc. — the hint just shows again next visit */
    }
  }

  // A citation chip jumps the reader back to its source (#03): the page is
  // matched by docId (the cited file, stable across title edits) with title as
  // the legacy fallback, then the view scrolls to the cited SECTION and
  // flashes it — not just the page top.
  function onJump(citation: { title: string; source?: string; anchor?: string }): void {
    const pages = toc?.sections.flatMap((s) => s.pages) ?? [];
    const p =
      (citation.source ? pages.find((pg) => pg.file === citation.source) : undefined) ??
      pages.find((pg) => pg.title === citation.title);
    if (!p || !manualId) return;
    if (citation.anchor) setJumpAnchor({ page: p.id, heading: citation.anchor });
    if (p.id !== currentId) {
      navigate(`/guides/${encodeURIComponent(manualId)}/${encodeURIComponent(p.id)}`);
    } else if (citation.anchor) {
      scrollToHeading(citation.anchor);
      setJumpAnchor(null);
    }
  }

  /** Scroll a rendered heading into view and flash it briefly. */
  function scrollToHeading(heading: string): boolean {
    const el = document.getElementById(slug(heading));
    if (!el) return false;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("guides-jump-flash");
    setTimeout(() => el.classList.remove("guides-jump-flash"), 1600);
    return true;
  }

  // Load the TOC once the configured manual id is known. A 404 -> home.
  useEffect(() => {
    if (!manualId) return;
    let live = true;
    void fetch(`/api/guides/${encodeURIComponent(manualId)}/toc`)
      .then((r) => {
        if (!r.ok) throw new Error(`toc ${r.status}`);
        return r.json() as Promise<Toc>;
      })
      .then((t) => {
        if (live) {
          setToc(t);
          setCollapsed(null); // re-init collapse state for the new manual
        }
      })
      .catch(() => {
        if (live) navigate("/", { replace: true });
      });
    return () => {
      live = false;
    };
  }, [manualId, navigate]);

  // The first "ready" page is the default landing page.
  const firstReady = useMemo(() => {
    if (!toc) return null;
    for (const s of toc.sections) {
      const p = s.pages.find((pg) => pg.status === "ready");
      if (p) return p.id;
    }
    return toc.sections[0]?.pages[0]?.id ?? null;
  }, [toc]);

  const currentId = pageId ?? firstReady;

  // Load the current page's content.
  useEffect(() => {
    if (!currentId || !manualId) return;
    // Page change invalidates the anchor's DOM range (#04) — drop it.
    clearAnchorHighlight();
    let live = true;
    setNotFound(false);
    void fetch(`/api/guides/${encodeURIComponent(manualId)}/page/${encodeURIComponent(currentId)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`page ${r.status}`);
        return r.json() as Promise<PageData>;
      })
      .then((p) => {
        if (live) setPage(p);
      })
      .catch(() => {
        if (live) setNotFound(true);
      });
    return () => {
      live = false;
    };
  }, [currentId, manualId]);

  // #03: once the jump target page has rendered, scroll to the cited section.
  useEffect(() => {
    if (!jumpAnchor || !page || page.id !== jumpAnchor.page) return;
    // Next frame: ReactMarkdown children are committed with their heading ids.
    const t = setTimeout(() => scrollToHeading(jumpAnchor.heading), 60);
    setJumpAnchor(null);
    return () => clearTimeout(t);
     
  }, [jumpAnchor, page]);

  // On-page TOC: the h2 headings of the current page.
  const headings = useMemo(() => {
    if (!page?.markdown) return [] as string[];
    return page.markdown
      .split("\n")
      .filter((l) => /^##\s+/.test(l))
      .map((l) => l.replace(/^##\s+/, "").trim());
  }, [page]);

  const allPages = useMemo(
    () => (toc ? toc.sections.flatMap((s) => s.pages) : []),
    [toc]
  );
  const idx = allPages.findIndex((p) => p.id === currentId);
  const prev = idx > 0 ? allPages[idx - 1] : null;
  const next = idx >= 0 && idx < allPages.length - 1 ? allPages[idx + 1] : null;
  const section = toc?.sections.find((s) => s.pages.some((p) => p.id === currentId));

  // Initialize collapse: an api-reference surface (many resource groups) starts
  // with every group collapsed EXCEPT the active page's group; other manuals stay
  // fully expanded. Runs once per manual (collapsed reset to null on toc load).
  useEffect(() => {
    if (!toc || collapsed !== null) return;
    if (toc.surface === "api-reference") {
      const open = new Set<string>([section?.id ?? ""]);
      setCollapsed(new Set(toc.sections.filter((s) => !open.has(s.id)).map((s) => s.id)));
    } else {
      setCollapsed(new Set());
    }
  }, [toc, collapsed, section?.id]);

  function toggleSection(id: string): void {
    setCollapsed((prevSet) => {
      const next2 = new Set(prevSet ?? []);
      if (next2.has(id)) next2.delete(id);
      else next2.add(id);
      return next2;
    });
  }

  if (!toc) return <div className="guides-loading">加载中…</div>;

  return (
    <div className="guides-shell">
      <header className="guides-topbar">
        <button className="guides-brand" type="button" onClick={() => navigate("/")}>
          {toc.brand?.product ?? "CycleCare"} <span className="guides-brand-zh">{toc.brand?.accent ?? "小环"}</span>
        </button>
        <nav className="guides-tabs">
          {toc.tabs.map((t) =>
            t.manual ? (
              // A tab linked to another manual (e.g. API Reference) switches the
              // reader to it — the top tabs are functional, config-driven.
              <button
                key={t.id}
                type="button"
                className="guides-tab link"
                onClick={() => {
                  setPage(null);
                  navigate(`/guides/${encodeURIComponent(t.manual ?? "")}`);
                }}
              >
                {t.label}
              </button>
            ) : (
              <span
                key={t.id}
                className={`guides-tab ${t.active ? "active" : ""} ${t.disabled ? "disabled" : ""}`}
                title={t.disabled ? "即将上线" : undefined}
              >
                {t.label}
              </span>
            )
          )}
        </nav>
        <button className="guides-exit" type="button" onClick={() => navigate("/")}>
          返回对话
        </button>
      </header>

      <div className={`guides-body ${askActive && dockOpen ? "has-dock" : ""} ${atLeast1c ? "ask-1c" : ""}`}>
        <aside className="guides-nav">
          {toc.sections.map((s) => {
            // A section is collapsible when it has many pages (e.g. API Reference
            // resource groups); single/few-page sections render flat.
            const collapsible = s.pages.length > 1 && toc.surface === "api-reference";
            const isCollapsed = collapsible && (collapsed?.has(s.id) ?? false);
            return (
              <div key={s.id} className="guides-nav-section">
                {collapsible ? (
                  <button type="button" className="guides-nav-title toggle" onClick={() => toggleSection(s.id)}>
                    <span className={`guides-nav-chev ${isCollapsed ? "" : "open"}`}>›</span>
                    {s.title}
                    <span className="guides-nav-count">{s.pages.length}</span>
                  </button>
                ) : (
                  <div className="guides-nav-title">{s.title}</div>
                )}
                {!isCollapsed &&
                  s.pages.map((p) => {
                    // Unknown icon names fall back to a document glyph (F1.6)
                    // instead of silently rendering nothing.
                    const Icon = p.icon ? (PAGE_ICONS[p.icon] ?? IconFile) : undefined;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`guides-nav-link ${p.id === currentId ? "active" : ""} ${
                          p.status === "planned" ? "planned" : ""
                        }`}
                        onClick={() => navigate(`/guides/${encodeURIComponent(manualId ?? "")}/${encodeURIComponent(p.id)}`)}
                      >
                        {p.method ? (
                          <span className={`guides-method m-${p.method.toLowerCase()}`}>{p.method === "WEBSOCKET" ? "WS" : p.method}</span>
                        ) : (
                          <span className="guides-nav-ic">{Icon ? <Icon size={15} /> : null}</span>
                        )}
                        <span className="guides-nav-label">{p.method ? p.title.replace(/^\S+\s/, "") : p.title}</span>
                        {p.status === "planned" && <span className="guides-soon">即将上线</span>}
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </aside>

        <main className="guides-main" tabIndex={-1}>
          {/* #01 (stage 1b+): one-time first-touch hint — select-to-ask is an
              invisible capability until someone says it exists. */}
          {askActive && stageAtLeast(askStage, "1b") && !hintSeen && (
            <div className="guides-ask-hint" role="status">
              <span>💡 选中任意段落，就能问{toc.assistant?.name ?? "小环"}</span>
              <button type="button" className="guides-ask-hint-x" onClick={markHintSeen}>
                知道了 ×
              </button>
            </div>
          )}
          {page && (
            <>
              <div className="guides-crumb">
                {toc.title} <span className="sep">/</span> {section?.title ?? ""}
              </div>
              <h1 className="guides-h1">{page.title}</h1>
              {page.subtitle && <p className="guides-subtitle">{page.subtitle}</p>}
              {page.status === "planned" || (notFound && !page.markdown) ? (
                <div className="guides-planned">本页正在撰写中，即将上线。</div>
              ) : (
                (() => {
                  const prose = (
                    <div className="guides-prose md">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h2: ({ children }) => <h2 id={slug(textOf(children))}>{children}</h2>,
                          h3: ({ children }) => <h3 id={slug(textOf(children))}>{children}</h3>,
                          img: ({ src, alt }) => (
                            <img className="guides-img" src={src ?? ""} alt={alt ?? ""} loading="lazy" />
                          )
                        }}
                      >
                        {page.markdown}
                      </ReactMarkdown>
                    </div>
                  );
                  // In-doc ask: wrap the prose so selecting text offers the ask
                  // popover — or, on mobile below stage 1c, the honest degrade.
                  if (askActive) {
                    return (
                      <SelectionLayer onAsk={onAsk} a11y={atLeast1c}>
                        {prose}
                      </SelectionLayer>
                    );
                  }
                  if (askDegraded) {
                    return (
                      <SelectionLayer onAsk={() => undefined} degraded>
                        {prose}
                      </SelectionLayer>
                    );
                  }
                  return prose;
                })()
              )}

              <nav className="guides-prevnext">
                {prev ? (
                  <button type="button" onClick={() => navigate(`/guides/${encodeURIComponent(manualId ?? "")}/${encodeURIComponent(prev.id)}`)}>
                    <span className="dir">上一页</span>
                    <span className="ttl">{prev.title}</span>
                  </button>
                ) : (
                  <span />
                )}
                {next && (
                  <button
                    type="button"
                    className="nextbtn"
                    onClick={() => navigate(`/guides/${encodeURIComponent(manualId ?? "")}/${encodeURIComponent(next.id)}`)}
                  >
                    <span className="dir">下一页</span>
                    <span className="ttl">{next.title}</span>
                  </button>
                )}
              </nav>
            </>
          )}
        </main>

        {/* In-doc ask dock replaces the on-page TOC while a question is open.
            Stage 1b+ (#02): closing HIDES the dock instead of unmounting it, so
            the conversation (and its session id) survives close/reopen. */}
        {askActive && (stageAtLeast(askStage, "1b") ? dockEverOpened : dockOpen) && (
          <AskDock
            // Keyed by manual: switching manuals (Guides <-> API Reference) must
            // remount the dock — one sessionId must never mix two manuals' scopes.
            key={toc.manual}
            kbSource={toc.kbSource ?? toc.manual}
            manualTitle={toc.title}
            pageTitle={page?.title ?? toc.title}
            assistantName={toc.assistant?.name}
            assistantAvatar={toc.assistant?.avatar}
            ask={pendingAsk}
            stage={askStage}
            hidden={!dockOpen}
            onClose={() => {
              setDockOpen(false);
              setPendingAsk(null);
              if (stageAtLeast(askStage, "1b")) {
                // Keep the turn anchors for reopen/scroll-back; just unpaint.
                hideAnchorHighlight();
              } else {
                setDockEverOpened(false);
                // #04: the anchor lives only as long as the conversation it anchors.
                clearAnchorHighlight();
              }
              // #12 (stage 1c+): hand focus back to the reading pane.
              if (atLeast1c) document.querySelector<HTMLElement>(".guides-main")?.focus();
            }}
            onJump={onJump}
            onPinJump={scrollToAnchorHighlight}
            onTurnJump={(key) => scrollToAnchorHighlight(key)}
            onHandoff={
              stageAtLeast(askStage, "2")
                ? (message) => {
                    // R★.2: stash the context and return to the main line — the
                    // main chat auto-sends it (真执行 belongs there, not in a dock).
                    try {
                      sessionStorage.setItem("guides-handoff", message);
                    } catch {
                      /* storage unavailable — the user still lands in the main chat */
                    }
                    navigate("/");
                  }
                : undefined
            }
          />
        )}
        {/* #02: the closed conversation is one click away, not gone. A floating
            pill (not inside the TOC rail) so it survives every viewport — the
            TOC is display:none below 1100px, which would strand mobile users. */}
        {askActive && stageAtLeast(askStage, "1b") && dockEverOpened && !dockOpen && (
          <button
            type="button"
            className="guides-ask-reopen"
            onClick={() => {
              setDockOpen(true);
              showAnchorHighlight();
            }}
          >
            继续问{toc.assistant?.name ?? "小环"}
          </button>
        )}
        {!dockOpen && (
          <aside className="guides-toc">
            {headings.length > 0 && (
              <>
                <div className="guides-toc-title">本页目录</div>
                {headings.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className="guides-toc-link"
                    onClick={() => document.getElementById(slug(h))?.scrollIntoView({ behavior: "smooth" })}
                  >
                    {h}
                  </button>
                ))}
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
